// Titles for the references a pull request's conversation makes (the
// Links card), from its related issues: one small query, apart from the
// pull request's, that may fail on its own.
import { GraphQLFailure } from '../api/gh';
import { prRelatedQuery, type RelatedResult } from '../api/queries';
import { knownRefs, type KnownRef } from '../logic/links';

/** Runs a GraphQL document: `graphql` in the app. */
export type RunQuery = <T>(query: string, variables: Record<string, unknown>) => Promise<T>;

/** What reading the titles came to: the titles, or a failure that may (`retry`) or will not go away. */
export type Titles = { kind: 'read'; refs: KnownRef[] } | { kind: 'failed'; retry: boolean };

/** GitHub's codes for a query its schema cannot answer: asking again gets the same answer. */
const SCHEMA_ERRORS = new Set(['undefinedField', 'undefinedType', 'undefinedArgument', 'argumentLiteralsIncompatible']);

const schemaErrors = (e: unknown) => (e instanceof GraphQLFailure ? e.errors.filter((x) => SCHEMA_ERRORS.has(String(x.extensions?.code))) : []);

/** GitHub's answer for a field its schema lacks (an older GitHub Enterprise Server). */
function unknownField(e: unknown, field: string): boolean {
  return (
    e instanceof GraphQLFailure &&
    e.errors.some((x) => x.extensions?.fieldName === field || x.message.includes(`'${field}'`))
  );
}

/** A failure, kept for a later try unless the server's schema cannot answer the query. */
const failed = (e: unknown): Titles => ({ kind: 'failed', retry: !schemaErrors(e).length });

/**
 * The titles GitHub gives for the issues and pull requests related to a
 * pull request (`vars`: owner, repo, number). A server without
 * `closingIssuesReferences` is asked again without it, and gives titles
 * for the rest. Otherwise a failure leaves references untitled: one that
 * may pass (offline, a rate limit, GitHub's own error) is for the caller
 * to retry later; one the server's schema causes is final.
 */
export async function loadKnownRefs(run: RunQuery, vars: Record<string, unknown>): Promise<Titles> {
  const read = async (closing: boolean): Promise<Titles> => {
    const res = await run<RelatedResult>(prRelatedQuery(closing), vars);
    return { kind: 'read', refs: res.repository?.pullRequest ? knownRefs(res.repository.pullRequest) : [] };
  };
  try {
    return await read(true);
  } catch (e) {
    if (!unknownField(e, 'closingIssuesReferences')) return failed(e);
  }
  try {
    return await read(false);
  } catch (e) {
    return failed(e);
  }
}
